import type { ReactNode } from "react";
import { useState } from "react";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Shell for the Popover + Command "search-select" pattern that was hand-rolled
 * across the entity / connector / member selectors. It owns the open state and
 * the Popover/Command/CommandInput/List/Empty/Group scaffolding; callers supply
 * the trigger and the `CommandItem`s, so item rendering and selection UI (Check
 * vs Checkbox, custom rows) stay with the caller.
 *
 * `trigger` receives the open state (for `aria-expanded` / chevron rotation).
 * `children` receives a `close` callback and the current search `query`: single-
 * select callers call `close` after applying the selection; multi-select callers
 * ignore it so the popover stays open. The `query` lets callers offer a
 * "create <query>" row for values that aren't in the list.
 */
export function Combobox({
	trigger,
	searchPlaceholder,
	emptyText,
	contentClassName,
	align = "start",
	onQueryChange,
	children,
}: {
	trigger: (open: boolean) => ReactNode;
	searchPlaceholder: string;
	emptyText: ReactNode;
	contentClassName?: string;
	align?: "start" | "center" | "end";
	/**
	 * Observe the search text. Callers whose options come from a server-side
	 * search need the query outside `children` (to drive the fetch); the ones
	 * filtering a list already in memory can ignore it and let cmdk match on
	 * each item's `value`.
	 */
	onQueryChange?: (query: string) => void;
	children: (close: () => void, query: string) => ReactNode;
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const updateQuery = (next: string) => {
		setQuery(next);
		onQueryChange?.(next);
	};
	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) updateQuery("");
			}}
		>
			<PopoverTrigger asChild>{trigger(open)}</PopoverTrigger>
			<PopoverContent className={cn("p-0", contentClassName)} align={align}>
				{/*
				 * A server-searching caller has ALREADY filtered: its options are the
				 * response to `query`. Letting cmdk score them a second time drops
				 * every result the server matched on a field the item's `value` does
				 * not repeat — an entity found by `external_id` 4021 scores 0 against
				 * the value "Acme GmbH acme company" and vanishes, which is precisely
				 * the lookup the search was added to support. Callers that filter a
				 * list already in memory pass no `onQueryChange` and keep cmdk's
				 * matching, since for them it is the only filter there is.
				 */}
				<Command shouldFilter={!onQueryChange}>
					<CommandInput
						placeholder={searchPlaceholder}
						value={query}
						onValueChange={updateQuery}
					/>
					<CommandList>
						<CommandEmpty>{emptyText}</CommandEmpty>
						<CommandGroup>
							{children(() => {
								setOpen(false);
								updateQuery("");
							}, query)}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
